import styled from '@emotion/styled';
import { gettext } from '@eventbrite/i18n';
import {
    Button,
    ExpansionPanel,
    ExpansionPanelContent,
    ExpansionPanelTrigger,
    LoadingButton,
    Typography,
} from '@eventbrite/marmalade';
import React, { useEffect, useState } from 'react';

import { day as EDS_COLORS } from '@eventbrite/eds-settings';
import { LoadingSkeleton } from '@eventbrite/loading-skeleton';
import { useAppContext } from '../../../AppContext';
import { FloatingCardTestId } from './FloatingCard';
import { CardSection, EventDetails, LinkItems } from './shared/components';
import { FloatingCardLinkItem } from './shared/types';

export const MobileFloatingCard = ({
    addedEventDetails,
    isLoading,
    linkItems,
    onPrimaryActionClick,
    onSecondaryActionClick,
    primaryButtonText,
    shouldShimmerButtons,
    shouldDisableLinks,
    subtitle,
    primaryButtonClickHeapId,
    secondaryButtonClickHeapId,
}: {
    addedEventDetails: string[];
    isLoading?: boolean;
    shouldShimmerButtons?: boolean;
    linkItems: FloatingCardLinkItem[];
    onPrimaryActionClick: () => void;
    onSecondaryActionClick: () => void;
    primaryButtonText: string;
    shouldDisableLinks: boolean;
    subtitle: string | null;
    primaryButtonClickHeapId: string;
    secondaryButtonClickHeapId: string;
}) => {
    const { createdEventId, didCreateAccount, didCreateEvent, updateContext } =
        useAppContext();

    const [isHidden, setIsHidden] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const didScrollToBottom =
                window.scrollY >= document.body.offsetHeight * 0.75;
            setIsHidden(didScrollToBottom);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <CardContainer
            isHidden={isHidden}
            data-testid={FloatingCardTestId.Container}
        >
            <ExpansionPanel>
                <ExpansionPanelTrigger>
                    <TextContainer>
                        <TitleContainer>
                            <Typography
                                as="h1"
                                variant="display-md"
                                color={'neutral-1000'}
                            >
                                {gettext(
                                    'Your event is almost ready!',
                                ).toString()}
                            </Typography>
                            <Typography
                                as="p"
                                variant="body-lg"
                                color={'neutral-1000'}
                            >
                                {subtitle}
                            </Typography>
                        </TitleContainer>
                    </TextContainer>
                </ExpansionPanelTrigger>
                <ExpansionPanelContent>
                    <SectionContainer>
                        <CardSection
                            title={gettext('What we included: ').toString()}
                            body={<EventDetails details={addedEventDetails} />}
                        />

                        <CardSection
                            title={gettext(
                                'Add more to your event: ',
                            ).toString()}
                            isLoading={shouldDisableLinks}
                            aria-hidden={shouldDisableLinks}
                            onClick={
                                !createdEventId && !didCreateAccount
                                    ? (e: React.MouseEvent<HTMLDivElement>) => {
                                          updateContext({
                                              isModalShowing: true,
                                          });
                                          e.preventDefault();
                                      }
                                    : undefined
                            }
                            body={<LinkItems links={linkItems} />}
                        />
                    </SectionContainer>

                    <ButtonsContainer
                        isFull={!didCreateEvent}
                        data-testid={FloatingCardTestId.Actions}
                    >
                        <ButtonContainer isFull={!didCreateEvent}>
                            {shouldShimmerButtons ? (
                                <LoadingSkeleton
                                    height={'40px'}
                                    width={'100%'}
                                />
                            ) : (
                                <LoadingButton
                                    data-testid={FloatingCardTestId.PrimaryCTA}
                                    isLoading={!!isLoading}
                                    disabled={isLoading}
                                    variant="primary"
                                    onClick={onPrimaryActionClick}
                                    data-heap-id={primaryButtonClickHeapId}
                                >
                                    {primaryButtonText}
                                </LoadingButton>
                            )}
                        </ButtonContainer>

                        {didCreateEvent && (
                            <ButtonContainer isFull={!didCreateEvent}>
                                {shouldShimmerButtons ? (
                                    <LoadingSkeleton
                                        height={'40px'}
                                        width={'100%'}
                                    />
                                ) : (
                                    <Button
                                        disabled={isLoading}
                                        data-testid={
                                            FloatingCardTestId.SecondaryCTA
                                        }
                                        data-heap-id={
                                            secondaryButtonClickHeapId
                                        }
                                        variant="secondary"
                                        onClick={onSecondaryActionClick}
                                    >
                                        {gettext('Edit event')}
                                    </Button>
                                )}
                            </ButtonContainer>
                        )}
                    </ButtonsContainer>
                </ExpansionPanelContent>
            </ExpansionPanel>
        </CardContainer>
    );
};

const CardContainer = styled.div<{ isHidden: boolean }>`
    position: fixed;
    bottom: 8px;
    right: 0;

    background-color: ${EDS_COLORS['ui-200']};
    background-image: url(https://cdn.evbstatic.com/s3-build/perm_001/7b5b78/django/images/create/auto-creation/auto-create-floating-card-splatter1.png),
        url(https://cdn.evbstatic.com/s3-build/perm_001/72dbfb/django/images/create/auto-creation/auto-create-floating-card-splatter2.png);
    background-repeat: no-repeat, no-repeat;
    background-position: top right, top right;
    border-radius: 8px;
    box-shadow: 0px 4px 15px rgba(40, 44, 53, 0.06),
        0px 2px 2px rgba(40, 44, 53, 0.06);
    transition: all 0.3s ease-in-out;
    transform: ${(props) =>
        props.isHidden ? 'translateY(100%)' : 'translateY(0%)'};
`;

const TextContainer = styled.div`
    padding: 24px 24px 8px 24px;
`;

const TitleContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const SectionContainer = styled.div`
    display: flex;
    gap: 8px;
    padding: 24px 24px 8px 24px;
`;

const ButtonsContainer = styled.div<{
    isFull: boolean;
}>`
    display: flex;
    gap: 16px;
    width: 100vw;
    padding: 16px 8px;
    padding: ${(props) => (props.isFull ? '16px' : '16px 8px')};
`;

const ButtonContainer = styled.div<{ isFull: boolean }>`
    width: ${(props) => (props.isFull ? '100%' : '50%')};
    button {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;
